import React, { useEffect, useState } from 'react';
import { sparkToast, Checkbox, useReadOnly } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { resolveDatasetNameForRecord } from '@/features/datasets/utils/datasetCloneNames';

const ReviewStatusIndicator = ({
    record,
    client_id,
    firstDatasetOrBenchmark,
    currentUsecase,
    isBenchmark,
    llm_usecase_id,
    updateRecord,
    fetchValidationCounts,
    model_responses_id,
    test_response,
    onDataOverviewRefresh,
}) => {
    const { readOnly } = useReadOnly();
    const isReviewedFromRecord = record?.is_document_reviewed || record?.is_validated;
    const recordId = record?._id ?? record?.id;

    // Optimistic state: null = use record value; true/false = show immediately while saving
    const [optimisticReviewed, setOptimisticReviewed] = useState(null);
    const [saving, setSaving] = useState(false);

    const checked = optimisticReviewed !== null ? optimisticReviewed : isReviewedFromRecord;

    // Reset optimistic state when viewing a different record
    useEffect(() => {
        setOptimisticReviewed(null);
    }, [recordId]);

    // Clear optimistic state once the record prop reflects the update
    useEffect(() => {
        if (optimisticReviewed !== null && !!isReviewedFromRecord === optimisticReviewed) {
            setOptimisticReviewed(null);
        }
    }, [isReviewedFromRecord, optimisticReviewed]);

    const handleReviewStatusChange = (newChecked) => {
        const datasetName = resolveDatasetNameForRecord(
            record,
            currentUsecase || {
                datasets: isBenchmark ? [] : [firstDatasetOrBenchmark].filter(Boolean),
                benchmarks: isBenchmark ? [firstDatasetOrBenchmark].filter(Boolean) : [],
            },
            isBenchmark,
        );
        setOptimisticReviewed(newChecked);
        setSaving(true);
        data_from_server_post(
            "single-example/update-is-document-reviewed",
            {
                client_id: client_id,
                dataset: datasetName,
                is_benchmark: isBenchmark,
                record_id: recordId,
                is_document_reviewed: newChecked,
                llm_usecase_id: llm_usecase_id,
                request_example: record.request_example,
                model_responses_id: model_responses_id,
                test_response: test_response,
            },
            (result) => {
                if (result?.success) {
                    setSaving(false);
                    if (updateRecord) {
                        const recordToUpdate = model_responses_id ? result.model_responses_record : result.new_record;
                        updateRecord(recordToUpdate, isBenchmark);
                    }
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    // Review state feeds the task change tracking (Retrain
                    // primary button), so re-check it after a toggle.
                    onDataOverviewRefresh?.();
                } else {
                    setOptimisticReviewed(null);
                    setSaving(false);
                    sparkToast.error("Failed to update review status");
                }
            }
        );
    };

    const checkboxLabel = checked ? 'Reviewed' : 'Pending Review';
    const checkboxStatus = checked ? 'active' : 'inactive';

    return (
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%', minHeight: '40px', whiteSpace: 'nowrap' }}>
            <Checkbox
                label={checkboxLabel}
                status={checkboxStatus}
                disabled={readOnly || saving}
                onChange={(e) => handleReviewStatusChange(e.target.checked)}
            />
        </div>
    );
};

export default ReviewStatusIndicator;
